// Right-side slide-in panel for editing a workflow node
// VoiceSpectrum.Web.T2 · v10.0.8.5 · 2026-05-17 · VoiceSpectrum AI

import { useState, useEffect } from 'react';
import type { WorkflowNode } from './data';
import { shouldSuppressLangTabs } from './lib/cardHelpers';
import { isModifiedFromDefault } from './lib/diff';
import ModifiedPill from '../ModifiedPill';
import ConfirmCardSlider from './ConfirmCardSlider';

interface Props {
  node: WorkflowNode | null;
  open: boolean;
  onClose: () => void;
  onSave: (node: WorkflowNode) => Promise<void>;
}

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
];

const NODE_COLORS: Record<string, string> = {
  Identity: 'var(--stripe-identity)',
  Steps:    'var(--stripe-steps)',
  Connect:  'var(--stripe-connect)',
  Confirm:  'var(--stripe-confirm)',
};

export default function CardSlider({ node, open, onClose, onSave }: Props) {
  const [lang, setLang] = useState('en');
  const [draft, setDraft] = useState<WorkflowNode | null>(node);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);

  useEffect(() => {
    if (node) {
      setDraft(node);
      setDirty(false);
      setLang('en');
    }
  }, [node]);

  function markDirty() { setDirty(true); }

  function attemptClose() {
    if (dirty) {
      setShowDiscard(true);
    } else {
      onClose();
    }
  }

  async function handleSave() {
    if (!draft) return;
    setSaving(true);
    try {
      await onSave(draft);
      setDirty(false);
      onClose();
    } catch {
      // In production: show error toast
    } finally {
      setSaving(false);
    }
  }

  if (!open || !draft) return null;

  const suppressLangTabs = shouldSuppressLangTabs(draft);
  const typeColor = NODE_COLORS[draft.type] ?? '#94a3b8';

  function renderTextField(
    label: string,
    value: string,
    packDefault: string,
    onChange: (v: string) => void,
    placeholder?: string,
    id?: string,
  ) {
    const isModified = isModifiedFromDefault(value, packDefault);
    return (
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem', flexWrap: 'wrap' }}>
          <label htmlFor={id}>{label}</label>
          {isModified && <ModifiedPill lang={lang} />}
          {isModified && (
            <button
              type="button"
              style={{ fontSize: '0.75rem', color: 'var(--t2-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={() => { onChange(packDefault); markDirty(); }}
            >
              Reset
            </button>
          )}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--t2-ink-muted)', fontFamily: 'monospace', marginBottom: '0.375rem' }}>
          PACK DEFAULT: {packDefault ? <em>{packDefault}</em> : <em>(empty)</em>}
        </div>
        <textarea
          id={id}
          value={value}
          onChange={e => { onChange(e.target.value); markDirty(); }}
          placeholder={placeholder}
          style={{ minHeight: '72px' }}
        />
        {/* Preview panel — always rendered per spec */}
        <div style={{ background: 'var(--preview-bg)', border: '3px solid var(--preview-border)', color: 'var(--preview-ink)', borderRadius: '6px', padding: '8px 12px', marginTop: '8px', fontSize: '0.875rem' }}>
          {value || <em style={{ opacity: 0.5 }}>Preview will appear here</em>}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={attemptClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 390,
        }}
      />

      {/* Slider panel */}
      <div
        style={{
          position: 'fixed',
          right: 0, top: 0,
          width: 'var(--slider-width)',
          maxWidth: '100vw',
          height: '100vh',
          background: '#fff',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          zIndex: 400,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Slider header */}
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid var(--t2-border)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          flexShrink: 0,
        }}>
          <div style={{
            width: '10px', height: '10px', borderRadius: '50%',
            background: typeColor, flexShrink: 0,
          }} />
          <span style={{ fontWeight: 700, fontSize: '0.75rem', color: typeColor, textTransform: 'uppercase' }}>
            {draft.type}
          </span>
          <span style={{ fontWeight: 600, flex: 1 }}>{draft.name}</span>
          {dirty && (
            <span style={{
              fontSize: '0.75rem', fontWeight: 600,
              background: 'var(--dirty-tint)',
              color: 'var(--dirty-shadow)',
              border: '1px solid var(--dirty-shadow)',
              borderRadius: '9999px',
              padding: '2px 8px',
            }}>
              Unsaved changes
            </span>
          )}
          <button
            onClick={attemptClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: 'var(--t2-ink-muted)', padding: '4px' }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Slider body */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Enabled toggle — D-09 */}
          {draft.type !== 'Confirm' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid var(--t2-border)' }}>
              <label>Enabled</label>
              <label className="toggle">
                <input
                  type="checkbox"
                  role="switch"
                  checked={draft.enabled}
                  onChange={e => { setDraft(d => d ? { ...d, enabled: e.target.checked } : d); markDirty(); }}
                />
                <span className="toggle-track" />
                <span className="toggle-thumb" />
              </label>
            </div>
          )}

          {/* Confirm node: delegate to ConfirmCardSlider */}
          {draft.type === 'Confirm' && (
            <ConfirmCardSlider
              node={draft}
              lang={lang}
              onLangChange={setLang}
              onChange={setDraft}
              onMarkDirty={markDirty}
            />
          )}

          {/* Other node types */}
          {draft.type !== 'Confirm' && (
            <div style={{ padding: '1.25rem' }}>
              {/* Language tabs — D-59 suppression */}
              {!suppressLangTabs && (
                <div style={{ display: 'flex', borderBottom: '1px solid var(--t2-border)', marginBottom: '1.25rem' }}>
                  {LANGUAGES.map(l => (
                    <button
                      key={l.code}
                      onClick={() => setLang(l.code)}
                      style={{
                        padding: '0.5rem 1.25rem',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        border: 'none',
                        borderBottom: lang === l.code ? '2px solid var(--t2-accent)' : '2px solid transparent',
                        background: 'none',
                        color: lang === l.code ? 'var(--t2-accent)' : 'var(--t2-ink-muted)',
                        cursor: 'pointer',
                      }}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Identity node fields */}
              {draft.type === 'Identity' && draft.identity.map((field, i) => (
                <div key={field.field_key} style={{ marginBottom: '1.5rem', borderBottom: '1px solid var(--t2-border)', paddingBottom: '1.5rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{field.label}</div>
                  {renderTextField(
                    `Prompt (${lang.toUpperCase()})`,
                    field.prompt[lang] ?? '',
                    '',
                    (v) => {
                      const identity = [...draft.identity];
                      identity[i] = { ...identity[i], prompt: { ...identity[i].prompt, [lang]: v } };
                      setDraft(d => d ? { ...d, identity } : d);
                    },
                    `Enter prompt for ${field.label} in ${lang.toUpperCase()}`,
                    `identity-prompt-${field.field_key}-${lang}`,
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={e => {
                        const identity = [...draft.identity];
                        identity[i] = { ...identity[i], required: e.target.checked };
                        setDraft(d => d ? { ...d, identity } : d);
                        markDirty();
                      }}
                      style={{ width: 'auto' }}
                    />
                    Required
                  </label>
                </div>
              ))}

              {/* Step-bearing node. v12.7.3 (#648): Request is the default now; a retired 'Steps' node still
                  renders its steps, or an existing document would silently lose its editor. */}
              {(draft.type === 'Request' || draft.type === 'Steps') && draft.steps.map((step, i) => (
                <div key={step.id} style={{ marginBottom: '1.5rem', borderBottom: '1px solid var(--t2-border)', paddingBottom: '1.5rem' }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Step: {step.label}</div>
                  {renderTextField(
                    `Prompt (${lang.toUpperCase()})`,
                    step.prompt[lang] ?? '',
                    '',
                    (v) => {
                      const steps = [...draft.steps];
                      steps[i] = { ...steps[i], prompt: { ...steps[i].prompt, [lang]: v } };
                      setDraft(d => d ? { ...d, steps } : d);
                    },
                    'Enter prompt text',
                    `step-prompt-${step.id}-${lang}`,
                  )}
                </div>
              ))}

              {/* Connect node */}
              {draft.type === 'Connect' && draft.connect && (
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Integration</label>
                    <input value={draft.connect.integration_ref} readOnly style={{ marginTop: '0.375rem', background: 'var(--t2-bg)' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Operation</label>
                    <input value={draft.connect.operation} readOnly style={{ marginTop: '0.375rem', background: 'var(--t2-bg)' }} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Slider footer — D-54: single global footer */}
        <div style={{
          padding: '1rem 1.25rem',
          borderTop: '1px solid var(--t2-border)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem',
          flexShrink: 0,
        }}>
          <button className="btn-ghost" onClick={attemptClose}>Cancel</button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Discard modal — D-22 */}
      {showDiscard && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 'var(--radius-lg)', padding: '2rem', maxWidth: '400px', width: '90%', boxShadow: 'var(--shadow-lg)' }}>
            <h3 style={{ marginBottom: '0.75rem' }}>Discard unsaved changes?</h3>
            <p style={{ color: 'var(--t2-ink-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              You have unsaved changes. If you leave now, your changes will be lost.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setShowDiscard(false)}>Keep editing</button>
              <button className="btn-danger" onClick={() => { setShowDiscard(false); onClose(); }}>Discard changes</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
